import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { Container } from "reactstrap";
import SecondaryButton from "../../../components/Common/SecondaryButton";
import DeleteConfirmationModal from "../../../components/Common/DeleteConfirmationModal";
import { get, post } from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import logoLight from "../../../assets/images/logo-light.png";
import tickIcon from "../../../assets/images/support/tick-circle-white.svg";

/* -------------------------------------------------------------------------
 * WeeklySettlementInvoicePage — standalone invoice document view.
 *
 * Unlike the other admin detail pages this isn't dropped inside the sidebar
 * shell — it's meant to be its own printable/emailable page (its own top
 * bar, centered document width), the way an emailed invoice link would open.
 *
 * TODO(api): confirm the real endpoint keys once available:
 *   - endpoints.adminSettlementInvoice   (GET  — fetch one invoice)
 *   - endpoints.adminSettlementMarkPaid  (POST — mark as paid)
 *   - endpoints.adminSettlementSend      (POST — email the invoice)
 *   - endpoints.adminSettlementDownload  (GET  — PDF/CSV export, ?format=)
 * Until then this renders with the shape below as a visual fallback so the
 * layout is complete and ready to wire up.
 * ---------------------------------------------------------------------- */

const FALLBACK_INVOICE = {
  restaurant: {
    name: "Restaurant Name",
    address: "Address on file",
    email: "owner@example.com",
    phone: "N/A",
    logo: null,
  },
  vendor_id: null,
  invoice_number: "—",
  settlement_period_label: "—",
  invoice_date: "—",
  due_date: "—",
  invoice_type: "Weekly Settlement",
  status: "pending",
  fee_rate_pct: 3,
  eligible_member_spend: 0,
  platform_fee_due: 0,
  performance: {
    eligible_member_spend: 0,
    member_transactions: 0,
    unique_members: 0,
    average_ticket: 0,
    new_members: 0,
    returning_members: 0,
  },
  rewards: {
    points_issued: 0,
    vouchers_redeemed: 0,
    voucher_value_redeemed: 0,
    active_vouchers: 0,
  },
};

const currency = (value) =>
  `$${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const isApiSuccess = (response) => {
  if (!response) return false;
  if (typeof response.data?.success !== "undefined") return response.data.success;
  if (typeof response.data?.status !== "undefined") return response.data.status;
  return response.status >= 200 && response.status < 300;
};

const extractPayload = (response) => {
  if (!response) return null;
  if (typeof response.data?.data !== "undefined") return response.data.data;
  return response.data || null;
};

const transformInvoiceData = (apiData) => {
  if (!apiData) return null;
  const perf = apiData.performance_summary || {};
  const calc = apiData.settlement_calculation || {};
  const invoice = apiData.invoice || {};
  const period = apiData.period || {};
  
  return {
    restaurant: {
      name: apiData.restaurant?.title || apiData.restaurant?.legal_business_name || "Unknown",
      address: apiData.restaurant?.address || "",
      email: apiData.restaurant?.email || "",
      phone: apiData.restaurant?.phone || "",
      logo: apiData.restaurant?.logo || null,
    },
    vendor_id: apiData.restaurant?.vendor_listing_id || null,
    invoice_number: invoice.invoice_number || "—",
    settlement_period_label: period.start_date && period.end_date 
      ? `${formatDateShort(period.start_date)} – ${formatDateShort(period.end_date)}`
      : "—",
    invoice_date: invoice.invoice_date ? formatDateShort(invoice.invoice_date) : "—",
    due_date: invoice.due_date ? formatDateShort(invoice.due_date) : "—",
    invoice_type: formatInvoiceType(invoice.invoice_type),
    status: apiData.status || "pending",
    fee_rate_pct: calc.platform_fee_rate || 3,
    eligible_member_spend: calc.eligible_member_spend || perf.eligible_member_spend || 0,
    platform_fee_due: calc.platform_fee_due || apiData.invoice_total || 0,
    performance: {
      eligible_member_spend: calc.eligible_member_spend || perf.eligible_member_spend || 0,
      member_transactions: perf.total_transactions || 0,
      unique_members: perf.total_customers || 0,
      average_ticket: perf.eligible_member_spend && perf.total_transactions 
        ? perf.eligible_member_spend / perf.total_transactions 
        : 0,
      new_members: 0, // Not provided in API response
      returning_members: 0, // Not provided in API response
    },
    rewards: {
      points_issued: 0, // Not provided in API response
      vouchers_redeemed: perf.total_redemptions || 0,
      voucher_value_redeemed: perf.total_redeemed_value || 0,
      active_vouchers: 0, // Not provided in API response
    },
  };
};

const formatDateShort = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const formatInvoiceType = (value) => {
  if (!value) return "Weekly Settlement";
  // Convert underscores to spaces and capitalize each word
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

/* ============ small inline icons (kept self-contained — no new asset files needed) ============ */

const IconWrap = ({ children, bg }) => (
  <span className="inv-icon-circle" style={{ background: bg }}>{children}</span>
);

const svgProps = { width: 18, height: 18, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" };

const IconInvoice = () => (<svg {...svgProps}><path d="M6 3h9l3 3v15H6z" /><path d="M9 8h6M9 12h6M9 16h4" /></svg>);
const IconDollarCircle = () => (<svg {...svgProps}><circle cx="12" cy="12" r="9" /><path d="M12 7v10M9.5 9.5c0-1.4 1.2-2.5 2.5-2.5s2.5 1 2.5 2c0 3-5 2-5 5 0 1 1.1 2 2.5 2s2.5-1.1 2.5-2.5" /></svg>);
const IconBag = () => (<svg {...svgProps}><path d="M6 8h12l1 12H5z" /><path d="M9 8V6a3 3 0 0 1 6 0v2" /></svg>);
const IconPeople = () => (<svg {...svgProps}><circle cx="9" cy="8" r="3" /><path d="M2 20c0-3.3 3.1-6 7-6s7 2.7 7 6" /><circle cx="17" cy="9" r="2.4" /><path d="M17.5 14.2c2.6.4 4.5 2.6 4.5 5.3" /></svg>);
const IconReceipt = () => (<svg {...svgProps}><path d="M6 3h12v18l-2-1-2 1-2-1-2 1-2-1-2 1z" /><path d="M9 8h6M9 12h6" /></svg>);
const IconPersonPlus = () => (<svg {...svgProps}><circle cx="10" cy="8" r="3.5" /><path d="M3 20c0-3.6 3.1-6.5 7-6.5s7 2.9 7 6.5" /><path d="M19 8v4M17 10h4" /></svg>);
const IconRefresh = () => (<svg {...svgProps}><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M21 4v4h-4" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" /><path d="M3 20v-4h4" /></svg>);
const IconGift = () => (<svg {...svgProps}><rect x="4" y="9" width="16" height="11" rx="1" /><path d="M2 9h20v3H2z" /><path d="M12 9v11M12 9C10 5 5 6 6 9c1 1.5 6 0 6 0zM12 9c2-4 7-3 6 0-1 1.5-6 0-6 0z" /></svg>);
const IconTicket = () => (<svg {...svgProps}><path d="M3 9a2 2 0 0 1 0-4h18a2 2 0 0 1 0 4 2 2 0 0 0 0 4 2 2 0 0 1 0 4H3a2 2 0 0 1 0-4 2 2 0 0 0 0-4z" /><path d="M12 5v2M12 11v2M12 17v2" /></svg>);
const IconTag = () => (<svg {...svgProps}><path d="M12 2h6a2 2 0 0 1 2 2v6l-9.5 9.5a2 2 0 0 1-2.8 0L3 15.3a2 2 0 0 1 0-2.8z" /><circle cx="16.5" cy="7.5" r="1.5" /></svg>);
const IconInfoCircle = () => (<svg {...svgProps}><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></svg>);
const IconDownload = () => (<svg {...svgProps}><path d="M12 4v11m0 0-4-4m4 4 4-4" /><path d="M5 19h14" /></svg>);

/* ============ small shared bits ============ */

const MetricItem = ({ icon, iconBg, label, value, valueColor }) => (
  <div className="inv-metric">
    <IconWrap bg={iconBg}>{icon}</IconWrap>
    <span className="inv-metric__label">{label}</span>
    <span className="inv-metric__value" style={valueColor ? { color: valueColor } : undefined}>{value}</span>
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    paid: { label: "Paid", cls: "inv-badge--green" },
    pending: { label: "Pending Payment", cls: "inv-badge--orange" },
    overdue: { label: "Overdue", cls: "inv-badge--red" },
  };
  const meta = map[(status || "").toLowerCase()] || map.pending;
  return <span className={`inv-badge ${meta.cls}`}>{meta.label}</span>;
};

const WeeklySettlementInvoicePage = () => {
  const { invoiceId } = useParams();
  const invoiceContentRef = useRef(null);

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);
  const [downloading, setDownloading] = useState(null); // "pdf" | "csv" | null
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false);

  useEffect(() => {
    toastr.options = {
      positionClass: "toast-bottom-left",
      timeOut: 5000,
      closeButton: true,
      progressBar: true,
    };
  }, []);

  const fetchInvoice = useCallback(async () => {
    try {
      setLoading(true);
      const endpoint = `/api/admin/settlement-invoices/${invoiceId}`;
      const response = await get(endpoint);
      if (isApiSuccess(response)) {
        const payload = extractPayload(response);
        if (payload) {
          const transformed = transformInvoiceData(payload);
          setInvoice(transformed);
        }
      }
    } catch (error) {
      console.error("Failed to fetch settlement invoice:", error);
      toastr.error("Failed to load the settlement invoice");
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  const data = invoice || FALLBACK_INVOICE;

  const handleMarkAsPaid = async () => {
    if (data.status === "paid") return;
    setShowMarkPaidModal(true);
  };

  const confirmMarkAsPaid = async () => {
    setShowMarkPaidModal(false);
    try {
      setMarking(true);
      const response = await post(endpoints.adminPlatformFeesMarkPaid, { 
        vendor_listing_id: data.vendor_id || invoiceId,
        mark_all_for_vendor: true 
      });
      if (isApiSuccess(response)) {
        toastr.success("Invoice marked as paid");
        setInvoice((prev) => ({ ...(prev || FALLBACK_INVOICE), status: "paid" }));
      } else {
        toastr.error("Failed to update invoice status");
      }
    } catch (error) {
      console.error("Failed to mark invoice as paid:", error);
      toastr.error("Failed to update invoice status");
    } finally {
      setMarking(false);
    }
  };

  const cancelMarkAsPaid = () => {
    setShowMarkPaidModal(false);
  };

  const handleDownload = async (format) => {
    try {
      setDownloading(format);
      
      if (format === "csv") {
        // Generate CSV client-side
        const csvRows = [
          ["Invoice Number", data.invoice_number],
          ["Restaurant", data.restaurant?.name],
          ["Invoice Date", data.invoice_date],
          ["Due Date", data.due_date],
          ["Status", data.status],
          [],
          ["Performance Summary"],
          ["Eligible Member Spend", currency(performance.eligible_member_spend)],
          ["Member Transactions", performance.member_transactions],
          ["Unique Members", performance.unique_members],
          ["Average Ticket", currency(performance.average_ticket)],
          [],
          ["Settlement Calculation"],
          ["Eligible Member Spend", currency(data.eligible_member_spend)],
          ["Platform Fee Rate", `${data.fee_rate_pct}%`],
          ["Platform Fee Due", currency(data.platform_fee_due)],
          [],
          ["Rewards Activity"],
          ["Vouchers Redeemed", rewards.vouchers_redeemed],
          ["Voucher Value Redeemed", currency(rewards.voucher_value_redeemed)],
        ];
        const csv = csvRows.map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${data.invoice_number || "invoice"}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else if (format === "pdf") {
        // Generate PDF using html2canvas and jspdf - always capture at desktop width
        if (!invoiceContentRef.current) {
          toastr.error("Invoice content not available");
          return;
        }

        // A4 dimensions in mm: 210 x 297
        const pdf = new jsPDF("p", "mm", "a4");
        const pdfWidth = pdf.internal.pageSize.getWidth(); // 210mm

        // Fixed desktop width for consistent PDF generation
        const desktopWidth = 1200; // pixels

        // Temporarily add PDF capture class and show the PDF header
        const originalClass = invoiceContentRef.current.className;
        invoiceContentRef.current.className = 'inv-pdf-capture';
        
        const headerElement = invoiceContentRef.current.querySelector('.inv-pdf-header');
        const originalHeaderDisplay = headerElement ? headerElement.style.display : '';
        if (headerElement) {
          headerElement.style.display = 'flex';
        }

        // Capture at fixed desktop width without extra padding
        const canvas = await html2canvas(invoiceContentRef.current, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
          width: desktopWidth,
          windowWidth: desktopWidth,
        });

        // Restore original class and header visibility
        invoiceContentRef.current.className = originalClass;
        if (headerElement) {
          headerElement.style.display = originalHeaderDisplay;
        }

        const imgData = canvas.toDataURL("image/png");
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;

        // Convert canvas dimensions to mm (using 96 DPI: 1mm = 3.78px)
        const mmToPx = 3.78;
        const imgWidthMm = imgWidth / mmToPx;
        const imgHeightMm = imgHeight / mmToPx;

        // Calculate scaling to fit A4 width with proper margins
        const margin = 15; // 15mm margin on each side
        const availableWidth = pdfWidth - (margin * 2);
        const ratio = availableWidth / imgWidthMm;
        const scaledWidth = imgWidthMm * ratio;
        const scaledHeight = imgHeightMm * ratio;

        // Center horizontally
        const imgX = (pdfWidth - scaledWidth) / 2;
        const imgY = margin;

        pdf.addImage(imgData, "PNG", imgX, imgY, scaledWidth, scaledHeight);
        pdf.save(`${data.invoice_number || "invoice"}.pdf`);
      }
    } catch (error) {
      console.error(`Failed to download ${format}:`, error);
      toastr.error(`Failed to download the ${format.toUpperCase()}`);
    } finally {
      setDownloading(null);
    }
  };

  const performance = data.performance || FALLBACK_INVOICE.performance;
  const rewards = data.rewards || FALLBACK_INVOICE.rewards;

  const calcRows = useMemo(() => {
    const eligible = Number(data.eligible_member_spend || 0);
    const ratePct = Number(data.fee_rate_pct || 0);
    const feeDue = data.platform_fee_due != null ? Number(data.platform_fee_due) : eligible * (ratePct / 100);
    return [
      { label: "Eligible Member Spend", calc: "—", amount: currency(eligible) },
      { label: "Platform Fee Rate", calc: `${ratePct}%`, amount: "—" },
      { label: "Platform Fee Due", calc: `${currency(eligible)} × ${ratePct}%`, amount: currency(feeDue), highlight: true },
    ];
  }, [data]);

  if (loading && !invoice) {
    return (
      <div className="page-content rp-page-content">
        <Container fluid>

            <div className="rp-skeleton" style={{ width: 220, height: 28, marginBottom: 24 }} />
            <div className="rp-skeleton" style={{ width: "100%", height: 160, marginBottom: 16 }} />
            <div className="rp-skeleton" style={{ width: "100%", height: 120 }} />

        </Container>
      </div>
    );
  }

  return (
    <div className="page-content rp-page-content">
      <Container fluid>
        {/* ---- Title row ---- */}
        <div className="network-leakage-toolbar" style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h4 className="rp-page__title network-leakage-toolbar__title">Weekly Settlement Invoice</h4>
            <p className="rp-card__subtitle">Your weekly performance report and platform fee settlement.</p>
          </div>
          <div className="inv-title-row__actions">
            <SecondaryButton variant={data.status === "paid" ? "light-grey" : "light-orange"} onClick={handleMarkAsPaid} disabled={marking || data.status === "paid"} icon={tickIcon} loading={marking}>
              {data.status === "paid" ? "Paid" : "Mark as Paid"}
            </SecondaryButton>
          </div>
        </div>

        {/* ---- Invoice content for PDF generation ---- */}
        <div ref={invoiceContentRef}>
          {/* ---- PDF Header with Logo and Title (hidden in web view, shown in PDF) ---- */}
          <div className="inv-pdf-header" style={{ display: 'none' }}>
            <img src={logoLight} alt="OFL Logo" className="inv-pdf-header__logo" />
            <h1 className="inv-pdf-header__title">Invoice</h1>
          </div>

          {/* ---- Restaurant + invoice meta ---- */}
          <div className="rp-card inv-card">
            <div className="inv-meta-grid">
              <div className="inv-restaurant">
                <div className="inv-restaurant__logo">
                  {data.restaurant?.logo ? (
                    <img src={data.restaurant.logo} alt="" />
                  ) : (
                    <span>{(data.restaurant?.name || "R").slice(0, 1).toUpperCase()}</span>
                  )}
                </div>
                <div className="inv-restaurant__details">
                  <span className="inv-restaurant__name">{data.restaurant?.name}</span>
                  <span className="inv-restaurant__line">{data.restaurant?.address}</span>
                  <span className="inv-restaurant__line">{data.restaurant?.email}</span>
                  <span className="inv-restaurant__line">{data.restaurant?.phone}</span>
                </div>
              </div>

              <div className="inv-meta-list">
                <div className="inv-meta-row"><span>Invoice Number</span><strong>{data.invoice_number}</strong></div>
                <div className="inv-meta-row"><span>Settlement Period</span><strong>{data.settlement_period_label}</strong></div>
                <div className="inv-meta-row"><span>Invoice Date</span><strong>{data.invoice_date}</strong></div>
                <div className="inv-meta-row"><span>Due Date</span><strong>{data.due_date}</strong></div>
                <div className="inv-meta-row"><span>Invoice Type</span><strong>{data.invoice_type}</strong></div>
              </div>
            </div>
          </div>

          {/* ---- Amount due ---- */}
          <div className="rp-card inv-card inv-amount-card">
            <div className="inv-amount-grid">
              <div>
                <span className="inv-amount__label">Amount Due</span>
                <div className="inv-amount__value">{currency(data.platform_fee_due ?? calcRows[2].amount)}</div>
                <span className="inv-amount__sub">Platform Fee ({data.fee_rate_pct}% of Eligible Member Spend)</span>
              </div>
              <div className="inv-amount__status">
                <IconWrap bg="#e7faf3"><span className="inv-status-icon"><IconInvoice /></span></IconWrap>
                <div className="inv-amount__status-text">
                  <span className="inv-amount__status-label">Status</span>
                  <StatusBadge status={data.status} />
                  <span className="inv-amount__status-label" style={{ marginTop: 10 }}>Due Date</span>
                  <strong>{data.due_date}</strong>
                </div>
              </div>
            </div>
          </div>

          {/* ---- Weekly Restaurant Performance ---- */}
          <div className="rp-card inv-card">
            <div className="rp-card__header">
              <h2 className="rp-card__title inv-section-title">Weekly Restaurant Performance</h2>
            </div>
            <div className="inv-metric-row">
              <MetricItem icon={<IconDollarCircle />} iconBg="#e7faf3" label="Eligible Member Spend" value={currency(performance.eligible_member_spend)} />
              <MetricItem icon={<IconBag />} iconBg="#eaf1fd" label="Member Transactions" value={Number(performance.member_transactions || 0).toLocaleString()} />
              <MetricItem icon={<IconPeople />} iconBg="#f1ecfc" label="Unique OFL Members" value={Number(performance.unique_members || 0).toLocaleString()} />
              <MetricItem icon={<IconReceipt />} iconBg="#fdf2e3" label="Average Ticket" value={currency(performance.average_ticket)} />
              <MetricItem icon={<IconPersonPlus />} iconBg="#e7faf3" label="New Members" value={Number(performance.new_members || 0).toLocaleString()} />
              <MetricItem icon={<IconRefresh />} iconBg="#eaf1fd" label="Returning Members" value={Number(performance.returning_members || 0).toLocaleString()} />
            </div>
          </div>

          {/* ---- Rewards Activity ---- */}
          <div className="rp-card inv-card">
            <div className="rp-card__header">
              <h2 className="rp-card__title inv-section-title">Rewards Activity</h2>
            </div>
            <div className="inv-metric-row inv-metric-row--four">
              <MetricItem icon={<IconGift />} iconBg="#e7faf3" label="Points Issued" value={`${Number(rewards.points_issued || 0).toLocaleString()} pts`} valueColor="#1ba97f" />
              <MetricItem icon={<IconTicket />} iconBg="#eaf1fd" label="Vouchers Redeemed" value={Number(rewards.vouchers_redeemed || 0).toLocaleString()} />
              <MetricItem icon={<IconDollarCircle />} iconBg="#f1ecfc" label="Voucher Value Redeemed" value={currency(rewards.voucher_value_redeemed)} valueColor="#7b4fd6" />
              <MetricItem icon={<IconTag />} iconBg="#fdf2e3" label="Active Vouchers" value={Number(rewards.active_vouchers || 0).toLocaleString()} valueColor="var(--text-orange)" />
            </div>
          </div>

          {/* ---- Settlement Calculation ---- */}
          <div className="rp-card inv-card">
            <div className="rp-card__header">
              <h2 className="rp-card__title inv-section-title">Settlement Calculation</h2>
            </div>
            <div className="inv-calc-table">
              <div className="inv-calc-row inv-calc-row--header">
                <span>Description</span>
                <span>Calculation</span>
                <span>Amount (USD)</span>
              </div>
              {calcRows.map((row) => (
                <div key={row.label} className={`inv-calc-row ${row.highlight ? "inv-calc-row--total" : ""}`}>
                  <span data-label="Description">{row.label}</span>
                  <span data-label="Calculation">{row.calc}</span>
                  <span data-label="Amount (USD)" className={row.highlight ? "inv-calc-amount--positive" : ""}>{row.amount}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ---- Notes + downloads ---- */}
        <div className="inv-footer-row">
          <div className="inv-notes">
            <IconInfoCircle />
            <div>
              <span className="inv-notes__title">Notes</span>
              <p className="inv-notes__text">
                This invoice covers the OFL Rewards platform fee ({data.fee_rate_pct}%) for the settlement period
                shown above. Reward funding and voucher redemption statistics are provided for reporting purposes only.
              </p>
            </div>
          </div>
          <div className="inv-footer-row__actions">
            <SecondaryButton variant="light-grey" onClick={() => handleDownload("pdf")} disabled={downloading === "pdf"}>
              <IconDownload /> {downloading === "pdf" ? "Preparing…" : "Download PDF"}
            </SecondaryButton>
            <SecondaryButton variant="light-grey" onClick={() => handleDownload("csv")} disabled={downloading === "csv"}>
              <IconDownload /> {downloading === "csv" ? "Preparing…" : "Download CSV"}
            </SecondaryButton>
          </div>
        </div>
      </Container>

      {/* Mark as Paid Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showMarkPaidModal}
        title="Mark Invoice as Paid"
        message="Are you sure you want to mark this invoice as paid? This action cannot be undone."
        onCancel={cancelMarkAsPaid}
        onConfirm={confirmMarkAsPaid}
        confirmButtonText="Mark as Paid"
        confirmButtonDisabled={marking}
      />
    </div>
  );
};

export default WeeklySettlementInvoicePage;